import React, { useEffect, useState } from 'react';
import { ModalProps, StepperProps } from 'types/Modal';
import { RequestAccessFormData } from 'types/Forms';
import LastStep from 'components/vertical-stepper/LastStep';
import { useNavigate } from 'react-router-dom';
import SelectDatasetStep from './SelectDatasetStep';
import VerticalStepper from 'components/vertical-stepper/VerticalStepper';
import SelectEnvironmentStep from './SelectEnvironmentStep';
import { DataType, ShareRequestItem } from 'types/Share';
import { NDialog, NLink } from '@innersource-nn/novoui-react';
import ShareItemsTable from './ShareItemsTable';

export interface DatasetAccessProps extends ModalProps {
    datasetName?: string;
    resourceUri?: string;
    count?: number;
}

export default function DatasetAccessModal({
    resourceUri,
    datasetName,
    visible,
    onClose,
    count,
}: DatasetAccessProps) {
    const navigate = useNavigate();

    const [creationError, setCreationError] = useState<string>('');

    const [shareUri, setShareUri] = useState('');

    const [formData, setFormData] = useState<RequestAccessFormData>({
        datasetUri: resourceUri ?? '',
        datasetLabel: datasetName ?? '',
    });
    const [activeStep, setActiveStep] = useState<number>(0);
    const [autoApproved, setAutoApproved] = useState(false);

    const handleClickSuccess = () => {
        if (autoApproved) {
            navigate(`/dataset/${formData.datasetUri}`);
        } else {
            navigate(`/share/${shareUri}`);
        }
        if (onClose) onClose();
    };
    const handleShareRequest = (shareRequestItem: ShareRequestItem) => {
        setShareUri(shareRequestItem.shareUri);
        setActiveStep(activeStep + 1);
    };

    const [steps, setSteps] = useState<StepperProps[]>([]);

    useEffect(() => {
        const firstStep = {
            label: 'Select dataset',
            content: (
                <SelectDatasetStep
                    setActiveStep={setActiveStep}
                    formData={formData}
                    setFormData={setFormData}
                    count={count ?? 100}
                />
            ),
        };
        const secondStep = {
            label: 'Select environment and target group',
            content: (
                <SelectEnvironmentStep
                    dataType={DataType['dataset']}
                    resourceUri={resourceUri ?? formData.datasetUri}
                    handleShareRequest={handleShareRequest}
                    buttonText="Next step"
                />
            ),
        };
        const thirdStep = {
            label: 'Technical specifications',
            content: (
                <ShareItemsTable
                    shareUri={shareUri}
                    resourceUri={resourceUri ?? formData.datasetUri}
                    handleNext={(data) => {
                        setAutoApproved(!!data?.autoApproved);
                        setCreationError(data?.creationError ?? '');
                        setActiveStep(3);
                    }}
                    onClose={onClose}
                    buttonLabel="Submit access request"
                />
            ),
        };
        if (resourceUri && datasetName) {
            setSteps([secondStep, thirdStep]);
        } else {
            setSteps([firstStep, secondStep, thirdStep]);
        }
    }, [count, datasetName, resourceUri, shareUri, formData]);

    return (
        visible &&
        onClose && (
            <NDialog
                isVisible={visible}
                closeDialog={onClose}
                backgroundClose
                size="large"
                showDividers={false}
                headline={
                    activeStep === 3
                        ? ''
                        : `Request access to ${formData.datasetLabel === '' ? 'dataset' : formData.datasetLabel}`
                }
            >
                {activeStep === 3 ? (
                    creationError === '' ? (
                        autoApproved ? (
                            <LastStep
                                title="Request approved!"
                                description={
                                    <>
                                        Request approved! Your request for{' '}
                                        <NLink
                                            className="font-bold underline"
                                            href={`/dataset/${formData.datasetUri}`}
                                            onClick={() => onClose()}
                                        >
                                            {formData.datasetLabel}
                                        </NLink>{' '}
                                        has been automatically approved since
                                        you are already an owner or steward of
                                        this dataset. You can continue managing
                                        or accessing the dataset as usual.
                                    </>
                                }
                                handleCancel={onClose}
                                primaryButtonAction={handleClickSuccess}
                                primaryButtonText="Go to dataset"
                                secondaryButtonText="Go back to home"
                                type="success"
                            />
                        ) : (
                            <LastStep
                                title="Request sent!"
                                description={
                                    <>
                                        Request sent! Your request for{' '}
                                        <NLink
                                            className="font-bold underline"
                                            href={`/dataset/${formData.datasetUri}`}
                                            onClick={() => onClose()}
                                        >
                                            {formData.datasetLabel}
                                        </NLink>{' '}
                                        has been sent, you will be notified here
                                        on datahub and through email as soon as
                                        your request has been processed by one
                                        of the owners/stewards.
                                    </>
                                }
                                handleCancel={onClose}
                                primaryButtonAction={handleClickSuccess}
                                primaryButtonText="See Share request"
                                secondaryButtonText="Go back to home"
                                type="success"
                            />
                        )
                    ) : (
                        <LastStep
                            title="Request failed"
                            type="error"
                            description={
                                <>
                                    We have encountered an error while
                                    requesting access to the dataset.
                                    {creationError}
                                </>
                            }
                            handleCancel={onClose}
                            primaryButtonAction={() =>
                                setActiveStep(activeStep - 1)
                            }
                            primaryButtonText="Go back to previous step"
                            secondaryButtonText="Cancel"
                        />
                    )
                ) : (
                    <VerticalStepper
                        steps={steps}
                        activeStep={activeStep}
                        setActiveStep={setActiveStep}
                    />
                )}
            </NDialog>
        )
    );
}
